import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Users, Store, MapPin, Stamp, Gift, TrendingUp, 
  Search, ChevronDown, ChevronUp, LogOut, Eye,
  Calendar, Mail, CreditCard, Activity, Download, Trash2, AlertTriangle, XCircle, Lock, UserX,
  Ticket, Plus, Copy, Clock, Check, X, Image, LayoutGrid
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OwnerDashboard = () => {
  const { user, logout, isOwner } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [expandedMerchant, setExpandedMerchant] = useState(null);
  const [merchantDetail, setMerchantDetail] = useState(null);
  
  // Delete confirmation states - 3 step process
  const [deleteStep, setDeleteStep] = useState(0); // 0: closed, 1: first confirm, 2: second confirm, 3: password
  const [merchantToDelete, setMerchantToDelete] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Change status modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [merchantToChangeStatus, setMerchantToChangeStatus] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  
  // Promo codes states
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    plan: 'growth',
    trial_days: 20,
    max_uses: '',
    expires_at: ''
  });
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [activeTab, setActiveTab] = useState('merchants'); // 'merchants', 'promos', 'carousel'
  
  // Carousel states
  const [carouselCandidates, setCarouselCandidates] = useState([]);

  useEffect(() => {
    if (!isOwner) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isOwner, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, merchantsRes, promosRes, carouselRes] = await Promise.all([
        axios.get(`${API}/owner/stats`),
        axios.get(`${API}/owner/merchants`),
        axios.get(`${API}/owner/promo-codes`),
        axios.get(`${API}/owner/carousel-candidates`)
      ]);
      setStats(statsRes.data);
      setMerchants(merchantsRes.data.merchants);
      setPromoCodes(promosRes.data.promo_codes || []);
      setCarouselCandidates(carouselRes.data.merchants || []);
    } catch (error) {
      console.error('Error fetching owner data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createPromoCode = async () => {
    setCreatingPromo(true);
    try {
      const data = {
        plan: promoForm.plan,
        trial_days: parseInt(promoForm.trial_days),
        max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses) : null,
        expires_at: promoForm.expires_at || null
      };
      
      await axios.post(`${API}/owner/promo-codes`, data);
      toast.success('Código promocional creado');
      setPromoModalOpen(false);
      setPromoForm({ plan: 'growth', trial_days: 20, max_uses: '', expires_at: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear código');
    } finally {
      setCreatingPromo(false);
    }
  };

  const updatePromoCode = async (codeId, updates) => {
    try {
      await axios.patch(`${API}/owner/promo-codes/${codeId}`, updates);
      toast.success('Código actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar código');
    }
  };

  const deletePromoCode = async (codeId) => {
    if (!window.confirm('¿Eliminar este código promocional?')) return;
    try {
      await axios.delete(`${API}/owner/promo-codes/${codeId}`);
      toast.success('Código eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar código');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Código copiado al portapapeles');
  };

  const toggleCarouselVisibility = async (merchantId, show) => {
    try {
      await axios.patch(`${API}/owner/carousel/${merchantId}?show=${show}`);
      toast.success(show ? 'Añadido al carrusel' : 'Quitado del carrusel');
      // Update local state
      setCarouselCandidates(prev => 
        prev.map(m => m.id === merchantId ? { ...m, show_in_carousel: show } : m)
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const downloadCSV = async (type) => {
    try {
      const response = await axios.get(`${API}/owner/export/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stampply_${type}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  };

  // Step 1: Click delete button - opens first modal
  const handleDeleteClick = (merchant) => {
    setMerchantToDelete(merchant);
    setDeleteStep(1);
    setDeletePassword('');
  };

  // Step 2: First confirmation - opens second modal
  const handleFirstConfirm = () => {
    setDeleteStep(2);
  };

  // Step 3: Second confirmation - opens password modal
  const handleSecondConfirm = () => {
    setDeleteStep(3);
  };

  // Step 4: Final delete with password verification
  const handleFinalDelete = async () => {
    if (!merchantToDelete || !deletePassword) {
      toast.error('Introduce tu contraseña para confirmar');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/owner/merchants/${merchantToDelete.id}`, {
        data: { password: deletePassword }
      });
      toast.success(`Comercio "${merchantToDelete.business_name}" eliminado correctamente`);
      setDeleteStep(0);
      setMerchantToDelete(null);
      setDeletePassword('');
      fetchData();
    } catch (error) {
      console.error('Error deleting merchant:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el comercio');
    } finally {
      setDeleting(false);
    }
  };

  // Cancel delete process
  const cancelDelete = () => {
    setDeleteStep(0);
    setMerchantToDelete(null);
    setDeletePassword('');
  };

  // Change merchant status
  const handleStatusChange = async () => {
    if (!merchantToChangeStatus || !newStatus) return;
    
    try {
      await axios.patch(`${API}/owner/merchants/${merchantToChangeStatus.id}/status`, {
        status: newStatus
      });
      toast.success(`Estado actualizado a "${getStatusLabel(newStatus)}"`);
      setStatusModalOpen(false);
      setMerchantToChangeStatus(null);
      setNewStatus('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar el estado');
    }
  };

  const openStatusModal = (merchant) => {
    setMerchantToChangeStatus(merchant);
    setNewStatus(merchant.status || 'active');
    setStatusModalOpen(true);
  };

  const searchMerchants = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (planFilter) params.append('plan', planFilter);
      
      const response = await axios.get(`${API}/owner/merchants?${params.toString()}`);
      setMerchants(response.data.merchants);
    } catch (error) {
      console.error('Error searching merchants:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      searchMerchants();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, planFilter]);

  const fetchMerchantDetail = async (merchantId) => {
    if (expandedMerchant === merchantId) {
      setExpandedMerchant(null);
      setMerchantDetail(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/owner/merchants/${merchantId}`);
      setMerchantDetail(response.data);
      setExpandedMerchant(merchantId);
    } catch (error) {
      console.error('Error fetching merchant detail:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPlanBadge = (plan) => {
    const colors = {
      free: 'bg-slate-100 text-slate-700',
      growth: 'bg-blue-100 text-blue-700',
      scale: 'bg-purple-100 text-purple-700',
      elite: 'bg-amber-100 text-amber-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[plan] || colors.free}`}>
        {plan?.toUpperCase() || 'FREE'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-700', label: 'Activo' },
      churned: { color: 'bg-red-100 text-red-700', label: 'Dado de baja' },
      suspended: { color: 'bg-orange-100 text-orange-700', label: 'Suspendido' },
      trial: { color: 'bg-blue-100 text-blue-700', label: 'Prueba' }
    };
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Activo',
      churned: 'Dado de baja',
      suspended: 'Suspendido',
      trial: 'Prueba'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <div>
              <h1 className="text-xl font-bold">Stampply Owner Panel</h1>
              <p className="text-slate-400 text-sm">Panel de administración</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-slate-800">
            <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={<Store className="w-6 h-6" />}
            label="Comercios"
            value={stats?.total_merchants || 0}
            color="bg-blue-500"
          />
          <StatCard 
            icon={<MapPin className="w-6 h-6" />}
            label="Ubicaciones"
            value={stats?.total_locations || 0}
            color="bg-green-500"
          />
          <StatCard 
            icon={<Stamp className="w-6 h-6" />}
            label="Sellos emitidos"
            value={stats?.total_stamps || 0}
            color="bg-amber-500"
          />
          <StatCard 
            icon={<Gift className="w-6 h-6" />}
            label="Vouchers"
            value={`${stats?.vouchers_redeemed || 0}/${stats?.vouchers_generated || 0}`}
            subtitle="canjeados"
            color="bg-purple-500"
          />
        </div>

        {/* Plan Distribution */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-slate-500" />
            Distribución por Plan
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['free', 'growth', 'scale', 'elite'].map(plan => (
              <div key={plan} className="text-center p-4 bg-slate-50 rounded-lg">
                <div className="text-2xl font-bold text-slate-900">
                  {stats?.by_plan?.[plan] || 0}
                </div>
                <div className="text-sm text-slate-500 capitalize">Plan {plan}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t flex items-center gap-2 text-sm text-slate-600">
            <TrendingUp className="w-4 h-4 text-green-500" />
            <span>{stats?.new_last_30_days || 0} nuevos comercios en los últimos 30 días</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            variant={activeTab === 'merchants' ? 'default' : 'outline'}
            onClick={() => setActiveTab('merchants')}
          >
            <Users className="w-4 h-4 mr-2" />
            Comercios
          </Button>
          <Button
            variant={activeTab === 'promos' ? 'default' : 'outline'}
            onClick={() => setActiveTab('promos')}
          >
            <Ticket className="w-4 h-4 mr-2" />
            Códigos Promocionales
          </Button>
          <Button
            variant={activeTab === 'carousel' ? 'default' : 'outline'}
            onClick={() => setActiveTab('carousel')}
          >
            <LayoutGrid className="w-4 h-4 mr-2" />
            Carrusel Home
          </Button>
        </div>

        {/* Carousel Section */}
        {activeTab === 'carousel' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-slate-500" />
                Carrusel de clientes en la Home
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Selecciona qué logos de clientes aparecerán en la página principal como referencia
              </p>
            </div>

            {carouselCandidates.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Image className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay clientes con logo</p>
                <p className="text-sm">Los clientes deben subir un logo para aparecer aquí</p>
              </div>
            ) : (
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {carouselCandidates.map((merchant) => (
                    <div 
                      key={merchant.id}
                      className={`relative border-2 rounded-xl p-4 transition-all ${
                        merchant.show_in_carousel 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {/* Logo */}
                      <div className="aspect-video bg-white rounded-lg border border-slate-100 flex items-center justify-center p-2 mb-3">
                        <img 
                          src={merchant.logo_url && merchant.logo_url.startsWith('http') ? merchant.logo_url : `${process.env.REACT_APP_BACKEND_URL}${merchant.logo_url || ''}`}
                          alt={merchant.business_name}
                          className="max-h-16 max-w-full object-contain"
                          onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><rect width="24" height="24" rx="4"/><text x="12" y="16" text-anchor="middle" font-size="12" fill="%23666">?</text></svg>'; }}
                        />
                      </div>
                      
                      {/* Info */}
                      <p className="font-medium text-slate-900 text-sm truncate mb-1">
                        {merchant.business_name}
                      </p>
                      <p className="text-xs text-slate-500 mb-3">
                        Plan {merchant.plan}
                      </p>
                      
                      {/* Toggle */}
                      <Button
                        size="sm"
                        variant={merchant.show_in_carousel ? "default" : "outline"}
                        className={`w-full ${merchant.show_in_carousel ? 'bg-green-600 hover:bg-green-700' : ''}`}
                        onClick={() => toggleCarouselVisibility(merchant.id, !merchant.show_in_carousel)}
                      >
                        {merchant.show_in_carousel ? (
                          <><Check className="w-4 h-4 mr-1" /> En carrusel</>
                        ) : (
                          <><Plus className="w-4 h-4 mr-1" /> Añadir</>
                        )}
                      </Button>
                      
                      {/* Status indicator */}
                      {merchant.show_in_carousel && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Summary */}
                <div className="mt-4 pt-4 border-t text-sm text-slate-600">
                  <span className="font-medium">{carouselCandidates.filter(m => m.show_in_carousel).length}</span> de {carouselCandidates.length} clientes seleccionados para el carrusel
                </div>
              </div>
            )}
          </div>
        )}

        {/* Promo Codes Section */}
        {activeTab === 'promos' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
            <div className="p-4 border-b flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-slate-500" />
                  Códigos Promocionales
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Genera códigos para regalar días de prueba a nuevos usuarios
                </p>
              </div>
              <Button onClick={() => setPromoModalOpen(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo código
              </Button>
            </div>

            {promoCodes.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Ticket className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay códigos promocionales</p>
                <p className="text-sm">Crea tu primer código para empezar a compartirlo</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Código</th>
                      <th className="px-4 py-3 text-left">Plan</th>
                      <th className="px-4 py-3 text-left">Días</th>
                      <th className="px-4 py-3 text-left">Usos</th>
                      <th className="px-4 py-3 text-left">Caduca</th>
                      <th className="px-4 py-3 text-left">Estado</th>
                      <th className="px-4 py-3 text-left">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {promoCodes.map((promo) => {
                      const isExpired = promo.expires_at && new Date(promo.expires_at) < new Date();
                      const isMaxed = promo.max_uses && promo.uses >= promo.max_uses;
                      
                      return (
                        <tr key={promo.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <code className="bg-slate-100 px-2 py-1 rounded font-mono text-sm">
                                {promo.code}
                              </code>
                              <button
                                onClick={() => copyToClipboard(promo.code)}
                                className="text-slate-400 hover:text-slate-600"
                                title="Copiar"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {getPlanBadge(promo.plan)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {promo.trial_days} días
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {promo.uses || 0}
                            {promo.max_uses && <span className="text-slate-400">/{promo.max_uses}</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500">
                            {promo.expires_at ? (
                              <span className={isExpired ? 'text-red-500' : ''}>
                                {new Date(promo.expires_at).toLocaleDateString('es-ES')}
                              </span>
                            ) : 'Sin límite'}
                          </td>
                          <td className="px-4 py-3">
                            {!promo.active ? (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                Inactivo
                              </span>
                            ) : isExpired ? (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                                Caducado
                              </span>
                            ) : isMaxed ? (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-600">
                                Agotado
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-600">
                                Activo
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => updatePromoCode(promo.id, { active: !promo.active })}
                                title={promo.active ? 'Desactivar' : 'Activar'}
                              >
                                {promo.active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deletePromoCode(promo.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Merchants List */}
        {activeTab === 'merchants' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-500" />
              Listado de Comercios
            </h3>
            <div className="flex flex-wrap gap-2 items-center">
              <Button variant="outline" size="sm" onClick={() => downloadCSV('merchants')}>
                <Download className="w-4 h-4 mr-1" /> Exportar Comercios
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadCSV('locations')}>
                <Download className="w-4 h-4 mr-1" /> Exportar Ubicaciones
              </Button>
            </div>
          </div>
          
          <div className="p-4 border-b bg-slate-50 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg bg-white text-sm"
            >
              <option value="">Todos los planes</option>
              <option value="free">Free</option>
              <option value="growth">Growth</option>
              <option value="scale">Scale</option>
              <option value="elite">Elite</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Comercio</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Plan</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-left">Fecha Alta</th>
                  <th className="px-4 py-3 text-left">Uso</th>
                  <th className="px-4 py-3 text-left">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {merchants.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                      No se encontraron comercios
                    </td>
                  </tr>
                ) : (
                  merchants.map((merchant) => (
                    <>
                      <tr key={merchant.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center">
                              <Store className="w-4 h-4 text-slate-500" />
                            </div>
                            <span className="font-medium text-slate-900">{merchant.business_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{merchant.email}</td>
                        <td className="px-4 py-3">{getPlanBadge(merchant.plan)}</td>
                        <td className="px-4 py-3">
                          <button 
                            onClick={() => openStatusModal(merchant)}
                            className="hover:opacity-80 transition-opacity"
                            title="Cambiar estado"
                          >
                            {getStatusBadge(merchant.status)}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{formatDate(merchant.created_at)}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {merchant.campaigns_count || 0} camp. · {merchant.devices_count || 0} QR · {merchant.locations_count || 0} loc. · {merchant.stamps_count || 0} sellos
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchMerchantDetail(merchant.id)}
                              title="Ver detalles"
                            >
                              {expandedMerchant === merchant.id ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(merchant)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Eliminar comercio"
                              data-testid={`delete-merchant-${merchant.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Detail */}
                      {expandedMerchant === merchant.id && merchantDetail && (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 bg-slate-50">
                            <div className="space-y-4">
                              {/* Metrics Summary */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                <div className="bg-white rounded-lg border p-3 text-center">
                                  <div className="flex items-center justify-center gap-2 mb-1">
                                    <Users className="w-4 h-4 text-blue-500" />
                                    <span className="text-xs text-slate-500">Usuarios únicos</span>
                                  </div>
                                  <p className="text-2xl font-bold text-slate-800">
                                    {merchantDetail.unique_customers_count || 0}
                                  </p>
                                </div>
                                <div className="bg-white rounded-lg border p-3 text-center">
                                  <div className="flex items-center justify-center gap-2 mb-1">
                                    <Gift className="w-4 h-4 text-amber-500" />
                                    <span className="text-xs text-slate-500">Vouchers totales</span>
                                  </div>
                                  <p className="text-2xl font-bold text-slate-800">
                                    {merchantDetail.total_vouchers || 0}
                                  </p>
                                </div>
                                <div className="bg-white rounded-lg border p-3 text-center">
                                  <div className="flex items-center justify-center gap-2 mb-1">
                                    <Check className="w-4 h-4 text-green-500" />
                                    <span className="text-xs text-slate-500">Canjeados</span>
                                  </div>
                                  <p className="text-2xl font-bold text-slate-800">
                                    {merchantDetail.redeemed_vouchers || 0}
                                  </p>
                                </div>
                                <div className="bg-white rounded-lg border p-3 text-center">
                                  <div className="flex items-center justify-center gap-2 mb-1">
                                    <TrendingUp className="w-4 h-4 text-purple-500" />
                                    <span className="text-xs text-slate-500">Tasa de canje</span>
                                  </div>
                                  <p className="text-2xl font-bold text-slate-800">
                                    {merchantDetail.redemption_rate || 0}%
                                  </p>
                                </div>
                              </div>

                              {/* Locations */}
                              <div>
                                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-green-500" />
                                  Ubicaciones ({merchantDetail.locations?.length || 0})
                                </h4>
                                {merchantDetail.locations?.length > 0 ? (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {merchantDetail.locations.map((loc) => (
                                      <div key={loc.id} className="p-2 bg-white rounded border text-sm">
                                        <p className="font-medium">{loc.name}</p>
                                        <p className="text-slate-500 text-xs">{loc.address}</p>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-400">Sin ubicaciones</p>
                                )}}
                              </div>

                              {/* Campaigns */}
                              <div>
                                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                  <Gift className="w-4 h-4 text-amber-500" />
                                  Campañas ({merchantDetail.campaigns?.length || 0})
                                </h4>
                                {merchantDetail.campaigns?.length > 0 ? (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {merchantDetail.campaigns.map((camp) => (
                                      <div key={camp.id} className="p-2 bg-white rounded border text-sm">
                                        <p className="font-medium">{camp.name}</p>
                                        <p className="text-slate-500 text-xs">
                                          {camp.goal} sellos → {camp.reward}
                                          {camp.active ? ' (Activa)' : ' (Inactiva)'}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-400">Sin campañas</p>
                                )}
                              </div>

                              {/* Recent Activity */}
                              <div>
                                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                  <Activity className="w-4 h-4 text-blue-500" />
                                  Actividad reciente
                                </h4>
                                {merchantDetail.recent_activity?.length > 0 ? (
                                  <div className="space-y-1">
                                    {merchantDetail.recent_activity.map((activity, idx) => (
                                      <div key={idx} className="text-xs text-slate-600 p-2 bg-white rounded border">
                                        <span className="text-slate-400">{formatDate(activity.created_at)}</span>
                                        {' · '}
                                        <span>{activity.customer_phone}</span>
                                        {' · '}
                                        <span className="font-medium">{activity.campaign_name}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-400">Sin actividad reciente</p>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </main>

      {/* PROMO CODE MODAL */}
      <Dialog open={promoModalOpen} onOpenChange={setPromoModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-primary" />
              Crear código promocional
            </DialogTitle>
            <DialogDescription>
              Este código permitirá a nuevos usuarios probar un plan de pago gratis durante un tiempo limitado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Plan a otorgar</Label>
              <select
                value={promoForm.plan}
                onChange={(e) => setPromoForm({ ...promoForm, plan: e.target.value })}
                className="w-full mt-1 px-3 py-2 border rounded-lg bg-white"
              >
                <option value="growth">Growth (29€/mes)</option>
                <option value="scale">Scale (95€/mes)</option>
                <option value="elite">Elite (395€/mes)</option>
              </select>
            </div>
            <div>
              <Label>Días de prueba</Label>
              <Input
                type="number"
                min="1"
                max="90"
                value={promoForm.trial_days}
                onChange={(e) => setPromoForm({ ...promoForm, trial_days: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Entre 1 y 90 días</p>
            </div>
            <div>
              <Label>Límite de usos (opcional)</Label>
              <Input
                type="number"
                min="1"
                placeholder="Ilimitado"
                value={promoForm.max_uses}
                onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Déjalo vacío para usos ilimitados</p>
            </div>
            <div>
              <Label>Fecha de caducidad del código (opcional)</Label>
              <Input
                type="date"
                value={promoForm.expires_at ? promoForm.expires_at.split('T')[0] : ''}
                onChange={(e) => setPromoForm({ ...promoForm, expires_at: e.target.value ? e.target.value + 'T23:59:59Z' : '' })}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Después de esta fecha el código no se podrá usar</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={createPromoCode} disabled={creatingPromo} className="btn-primary">
              {creatingPromo ? 'Creando...' : 'Crear código'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE MODAL - Step 1: First Confirmation */}
      <Dialog open={deleteStep === 1} onOpenChange={() => cancelDelete()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              ¿Eliminar comercio?
            </DialogTitle>
            <DialogDescription>
              Estás a punto de eliminar el comercio <strong>"{merchantToDelete?.business_name}"</strong> ({merchantToDelete?.email}).
              <br /><br />
              Esta acción eliminará TODOS los datos asociados: campañas, sellos, vouchers y configuraciones.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={cancelDelete}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleFirstConfirm}
              data-testid="confirm-delete-step1"
            >
              Sí, quiero eliminarlo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE MODAL - Step 2: Second Confirmation */}
      <Dialog open={deleteStep === 2} onOpenChange={() => cancelDelete()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              ¿Estás completamente seguro?
            </DialogTitle>
            <DialogDescription>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
                <p className="text-red-800 font-medium">
                  ⚠️ ATENCIÓN: Esta acción es IRREVERSIBLE
                </p>
                <p className="text-red-700 text-sm mt-2">
                  Se eliminarán permanentemente todos los datos del comercio "{merchantToDelete?.business_name}":
                </p>
                <ul className="text-red-700 text-sm mt-2 list-disc list-inside">
                  <li>Cuenta y configuración</li>
                  <li>Todas las campañas</li>
                  <li>Todos los dispositivos QR</li>
                  <li>Historial de sellos</li>
                  <li>Vouchers generados</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={cancelDelete}>
              No, cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleSecondConfirm}
              data-testid="confirm-delete-step2"
            >
              Sí, estoy seguro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE MODAL - Step 3: Password Verification */}
      <Dialog open={deleteStep === 3} onOpenChange={() => cancelDelete()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Lock className="w-5 h-5" />
              Verificación de seguridad
            </DialogTitle>
            <DialogDescription>
              Para confirmar la eliminación de <strong>"{merchantToDelete?.business_name}"</strong>, introduce tu contraseña de administrador.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="delete-password">Contraseña de dedalo71@gmail.com</Label>
            <Input
              id="delete-password"
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Introduce tu contraseña"
              className="mt-2"
              data-testid="delete-password-input"
            />
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={cancelDelete}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleFinalDelete}
              disabled={deleting || !deletePassword}
              data-testid="confirm-delete-final"
            >
              {deleting ? 'Eliminando...' : 'Eliminar definitivamente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* STATUS CHANGE MODAL */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserX className="w-5 h-5 text-slate-600" />
              Cambiar estado del comercio
            </DialogTitle>
            <DialogDescription>
              Comercio: <strong>{merchantToChangeStatus?.business_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <Label>Selecciona el nuevo estado:</Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'active', label: 'Activo', desc: 'Cliente activo y pagando', color: 'border-green-500 bg-green-50' },
                { value: 'churned', label: 'Dado de baja', desc: 'Dejó de pagar', color: 'border-red-500 bg-red-50' },
                { value: 'suspended', label: 'Suspendido', desc: 'Cuenta suspendida', color: 'border-orange-500 bg-orange-50' },
                { value: 'trial', label: 'Prueba', desc: 'En período de prueba', color: 'border-blue-500 bg-blue-50' }
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => setNewStatus(status.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    newStatus === status.value ? status.color + ' border-2' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <p className="font-medium text-sm">{status.label}</p>
                  <p className="text-xs text-slate-500">{status.desc}</p>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleStatusChange}>
              Guardar cambio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon, label, value, subtitle, color }) => (
  <div className="bg-white rounded-xl shadow-sm p-5">
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center text-white`}>
        {icon}
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        <div className="text-sm text-slate-500">{label}</div>
        {subtitle && <div className="text-xs text-slate-400">{subtitle}</div>}
      </div>
    </div>
  </div>
);

export default OwnerDashboard;
